import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Separator } from '../components/ui/separator';
import {
  User,
  Mail,
  Shield,
  Calendar,
  Building2,
  KeyRound,
  Bell,
  FolderOpen,
  Loader2,
  Check,
  Pencil,
  X
} from 'lucide-react';
import { apiErrorMessage } from '../lib/apiError';
import { toast } from 'sonner';
import api from '../lib/api';

const UserProfilePage = () => {
  const { user, updateProfile } = useAuth();
  const navigate = useNavigate();
  const [projects, setProjects] = useState([]);
  const [loadingProjects, setLoadingProjects] = useState(true);
  const [editing, setEditing] = useState(false);
  const [editName, setEditName] = useState('');
  const [saving, setSaving] = useState(false);

  const fetchProjects = useCallback(async () => {
    setLoadingProjects(true);
    try {
      const res = await api.get('/auth/profile/projects');
      setProjects(res.data);
    } catch {
      setProjects([]);
    } finally {
      setLoadingProjects(false);
    }
  }, []);

  useEffect(() => {
    fetchProjects();
  }, [fetchProjects]);

  const handleEditStart = () => {
    setEditName(user?.name || '');
    setEditing(true);
  };

  const handleEditCancel = () => {
    setEditing(false);
    setEditName('');
  };

  const handleSaveName = async () => {
    if (!editName.trim()) return;
    setSaving(true);
    try {
      await updateProfile(editName.trim());
      toast.success('Name updated successfully');
      setEditing(false);
    } catch (error) {
      toast.error(apiErrorMessage(error, 'Failed to update name'));
    } finally {
      setSaving(false);
    }
  };

  const formatRole = (role) => {
    return role?.split('_').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ') || '';
  };

  const getRoleBadgeColor = (role) => {
    switch (role) {
      case 'super_admin': return 'bg-red-100 text-red-700 border-red-200';
      case 'admin': return 'bg-blue-100 text-blue-700 border-blue-200';
      case 'member': return 'bg-slate-100 text-slate-700 border-slate-200';
      case 'plan': return 'bg-emerald-100 text-emerald-700 border-emerald-200';
      case 'collaborate': return 'bg-amber-100 text-amber-700 border-amber-200';
      case 'view': return 'bg-slate-100 text-slate-700 border-slate-200';
      default: return 'bg-slate-100 text-slate-600';
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'active':
        return <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200">Active</Badge>;
      case 'invited':
        return <Badge variant="outline" className="bg-amber-50 text-amber-700 border-amber-200">Invited</Badge>;
      case 'suspended':
        return <Badge variant="outline" className="bg-red-50 text-red-700 border-red-200">Suspended</Badge>;
      default:
        return <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200">Active</Badge>;
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    const d = new Date(dateStr);
    return d.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
  };

  const formatRelativeDate = (dateStr) => {
    if (!dateStr) return 'Never';
    const d = new Date(dateStr);
    const now = new Date();
    const diffMs = now - d;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  return (
    <div className="max-w-3xl mx-auto space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-slate-900">My Profile</h1>
        <p className="text-sm text-slate-500">View and manage your account details</p>
      </div>

      {/* Profile Card */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex items-start gap-6">
            {/* Avatar */}
            <div className="h-20 w-20 rounded-full bg-slate-100 flex items-center justify-center flex-shrink-0">
              <User className="h-10 w-10 text-slate-400" />
            </div>

            {/* Info */}
            <div className="flex-1 min-w-0 space-y-4">
              {/* Name */}
              <div>
                <Label className="text-xs text-slate-500 mb-1 block">Name</Label>
                {editing ? (
                  <div className="flex items-center gap-2">
                    <Input
                      value={editName}
                      onChange={(e) => setEditName(e.target.value)}
                      className="h-9 max-w-xs"
                      autoFocus
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') handleSaveName();
                        if (e.key === 'Escape') handleEditCancel();
                      }}
                    />
                    <Button size="sm" onClick={handleSaveName} disabled={saving || !editName.trim()}>
                      {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />}
                    </Button>
                    <Button size="sm" variant="ghost" onClick={handleEditCancel}>
                      <X className="h-4 w-4" />
                    </Button>
                  </div>
                ) : (
                  <div className="flex items-center gap-2">
                    <p className="text-lg font-semibold text-slate-900">{user?.name}</p>
                    <Button size="sm" variant="ghost" className="h-7 w-7 p-0" onClick={handleEditStart}>
                      <Pencil className="h-3 w-3" />
                    </Button>
                  </div>
                )}
              </div>

              {/* Email */}
              <div className="flex items-center gap-2">
                <Mail className="h-4 w-4 text-slate-400" />
                <span className="text-sm text-slate-700">{user?.email}</span>
              </div>

              {/* Role & Status */}
              <div className="flex items-center gap-3">
                <Badge variant="outline" className={getRoleBadgeColor(user?.role)}>
                  {formatRole(user?.role)}
                </Badge>
                {getStatusBadge(user?.account_status)}
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Account Details */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base">Account Details</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <div className="flex items-center gap-3">
              <Shield className="h-4 w-4 text-slate-400" />
              <div className="flex-1">
                <p className="text-xs text-slate-500">Role</p>
                <p className="text-sm font-medium text-slate-900">{formatRole(user?.role)}</p>
              </div>
            </div>
            <Separator />
            <div className="flex items-center gap-3">
              <Calendar className="h-4 w-4 text-slate-400" />
              <div className="flex-1">
                <p className="text-xs text-slate-500">Member since</p>
                <p className="text-sm font-medium text-slate-900">{formatDate(user?.created_at)}</p>
              </div>
            </div>
            {user?.organization_id && (
              <>
                <Separator />
                <div className="flex items-center gap-3">
                  <Building2 className="h-4 w-4 text-slate-400" />
                  <div className="flex-1">
                    <p className="text-xs text-slate-500">Organization</p>
                    <p className="text-sm font-medium text-slate-900">{user.organization_name || user.organization_id}</p>
                  </div>
                </div>
              </>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Quick Actions */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Card className="cursor-pointer hover:border-slate-300 transition-colors" onClick={() => navigate('/change-password')}>
          <CardContent className="pt-6 flex items-center gap-3">
            <div className="h-10 w-10 rounded-lg bg-blue-50 flex items-center justify-center flex-shrink-0">
              <KeyRound className="h-5 w-5 text-blue-600" />
            </div>
            <div>
              <p className="text-sm font-medium text-slate-900">Change Password</p>
              <p className="text-xs text-slate-500">Update your account password</p>
            </div>
          </CardContent>
        </Card>

        <Card className="cursor-pointer hover:border-slate-300 transition-colors" onClick={() => navigate('/email-notifications')}>
          <CardContent className="pt-6 flex items-center gap-3">
            <div className="h-10 w-10 rounded-lg bg-amber-50 flex items-center justify-center flex-shrink-0">
              <Bell className="h-5 w-5 text-amber-600" />
            </div>
            <div>
              <p className="text-sm font-medium text-slate-900">Notification Settings</p>
              <p className="text-xs text-slate-500">Manage email notification preferences</p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* My Projects */}
      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between gap-2">
            <div className="min-w-0">
              <CardTitle className="text-base">My Projects</CardTitle>
              <CardDescription>Projects you have been assigned to</CardDescription>
            </div>
            <Badge variant="outline" className="text-slate-500 shrink-0">
              {projects.length} project{projects.length !== 1 ? 's' : ''}
            </Badge>
          </div>
        </CardHeader>
        <CardContent>
          {loadingProjects ? (
            <div className="flex justify-center py-6">
              <Loader2 className="h-6 w-6 animate-spin text-slate-400" />
            </div>
          ) : projects.length === 0 ? (
            <div className="text-center py-6">
              <FolderOpen className="h-8 w-8 text-slate-300 mx-auto mb-2" />
              <p className="text-sm text-slate-400">No projects assigned yet</p>
              <p className="text-xs text-slate-400 mt-1">Contact your administrator to get access to projects</p>
            </div>
          ) : (
            <div className="space-y-2">
              {projects.map((p) => (
                <div
                  key={p.project_id}
                  className="flex items-center justify-between gap-2 p-3 rounded-lg border border-slate-100 hover:border-slate-200 transition-colors"
                >
                  <div className="flex items-center gap-3 min-w-0">
                    <div className="h-8 w-8 rounded bg-slate-100 flex items-center justify-center shrink-0">
                      <FolderOpen className="h-4 w-4 text-slate-500" />
                    </div>
                    <span className="text-sm font-medium text-slate-900 truncate min-w-0">{p.project_name}</span>
                  </div>
                  {p.assigned_at && (
                    <span className="text-xs text-slate-400 shrink-0">
                      Assigned {formatRelativeDate(p.assigned_at)}
                    </span>
                  )}
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default UserProfilePage;
